import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProjects } from "../../services/projectService";
import { formatDate } from "../../utils/formatDate";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("ongoing");

  const filteredProjects = projects.filter((project) => {
    if (statusFilter === "all") return true;
    return project.status === statusFilter;
  });

  function getStatusColor(status) {
    if (status === "new") return "bg-yellow-100 text-yellow-800";
    if (status === "ongoing") return "bg-green-100 text-green-800";
    if (status === "completed") return "bg-gray-100 text-gray-800";
    if (status === "on-hold") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  }

  useEffect(() => {
    async function fetchProjects() {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (err) {
        setError("Failed to load projects.");
      } finally {
        setLoading(false);
      }
    }
    fetchProjects();
  }, []);

  if (loading)
    return <div className="text-center text-gray-600">Loading projects...</div>;

  if (error)
    return <div className="text-center text-red-500">{error}</div>;

  return (
    <>
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold">Projects</h1>

        <Link
          to="/projects/add"
          className="inline-flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + New Project
        </Link>
      </div>

      {/* STATUS FILTER */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {["ongoing", "new", "completed", "on-hold", "all"].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1 rounded-full text-sm capitalize whitespace-nowrap ${
              statusFilter === status
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* MOBILE VIEW (CARDS) */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="bg-white rounded-xl shadow-sm p-4 border hover:shadow transition"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{project.name}</h3>
                  <p className="text-sm text-gray-500">
                    {project.clientInfo.name}
                  </p>
                </div>

                <span
                  className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                    project.status
                  )}`}
                >
                  {project.status}
                </span>
              </div>

              <div className="mt-3 text-sm text-gray-600">
                <p>
                  <strong>Start:</strong>{" "}
                  {formatDate(project.startDate)}
                </p>
                <p>
                  <strong>Due:</strong> {formatDate(project.deadline)}
                </p>
              </div>
            </Link>
          ))
        ) : (
          <p className="text-center text-gray-500">No projects found.</p>
        )}
      </div>

      {/* DESKTOP VIEW (TABLE) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full bg-white rounded-xl shadow-sm">
          <thead className="bg-gray-300 text-gray-800">
            <tr>
              <th className="py-3 px-4 text-left">#</th>
              <th className="py-3 px-4 text-left">Title</th>
              <th className="py-3 px-4 text-left">Client</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-center">Start Date</th>
              <th className="py-3 px-4 text-center">Due Date</th>
              <th className="py-3 px-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <tr
                  key={project.id}
                  className="border-t hover:bg-gray-50"
                >
                  <td className="py-2 px-4">{index + 1}</td>
                  <td className="py-2 px-4 font-medium">
                    {project.name}
                  </td>
                  <td className="py-2 px-4">
                    {project.clientInfo.name}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                        project.status
                      )}`}
                    >
                      {project.status}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">
                    {formatDate(project.startDate)}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {formatDate(project.deadline)}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <Link to={`/projects/${project.id}`}>
                      <ChevronRightIcon className="h-5 w-5 inline text-gray-600 hover:text-blue-600" />
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-6 text-center text-gray-500">
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ProjectsList;
